import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import BodyScanHeatmap from "@/components/BodyScanHeatmap";
import RoutineOverrideBuilder from "@/components/RoutineOverrideBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, Save, Upload as UploadIcon, FileJson, HeartPulse, Video } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Area, AreaChart } from "recharts";
import { useAuth } from "@/contexts/AuthContext";
import PitchTelemetryImporter from "@/components/PitchTelemetryImporter";
import BaselineStaffEntryCard from "@/components/BaselineStaffEntryCard";
import InjuryRiskPanel from "@/components/InjuryRiskPanel";
import InjuryEventLogger from "@/components/InjuryEventLogger";

const BASELINE_GROUPS = [
  { key: "personal_metadata", title: "Personal", fields: [
    ["year", "Year"], ["position", "Position"], ["dominant_arm", "Dominant Arm"],
    ["bats", "Bats"], ["throws", "Throws"], ["injury_history", "Injury History"]
  ]},
  { key: "mobility_rom", title: "Mobility & ROM", fields: [
    ["external_rotation", "External Rotation (°)"], ["internal_rotation", "Internal Rotation (°)"], ["total_arc_of_motion", "Total Arc (°)"]
  ]},
  { key: "humeral_torsion", title: "Humeral Torsion", fields: [
    ["humeral_torsion_dominant", "Dominant"], ["humeral_torsion_non_dominant", "Non-Dominant"], ["humeral_torsion_difference", "Difference"]
  ]},
  { key: "musculoskeletal_strength", title: "Rotational Strength", fields: [
    ["er_strength_dominant", "ER Dom"], ["er_strength_non_dominant", "ER Non-Dom"], ["er_strength_lsi", "ER LSI %"],
    ["ir_strength_dominant", "IR Dom"], ["ir_strength_non_dominant", "IR Non-Dom"], ["ir_strength_lsi", "IR LSI %"]
  ]},
  { key: "grip_strength", title: "Grip 90/90", fields: [
    ["grip_strength_dominant", "Dom"], ["grip_strength_non_dominant", "Non-Dom"], ["grip_strength_lsi", "LSI %"]
  ]},
  { key: "functional_power", title: "Functional Power", fields: [
    ["y_balance_dominant",     "Y-Balance · Dominant Reach (cm)"],
    ["y_balance_non_dominant", "Y-Balance · Non-Dom Reach (cm)"],
    ["y_balance_difference",   "Y-Balance · Asymmetry Δ (cm)"],
    ["broad_jump",             "Standing Broad Jump (in)"],
    ["vertical_leap",          "Vertical Leap (in)"]
  ]},
  { key: "speed_agility", title: "Speed & Agility", fields: [
    ["sixty_yd_dash",  "60 yd Sprint (sec)"],
    ["split_10",       "10 yd Split (sec)"],
    ["split_30",       "30.6-Yd Split — Home-to-First (sec)"],
    ["split_60",       "60 yd Split (sec)"],
    ["drill_5x10x5",   "5-10-5 Pro Agility Shuttle (sec)"],
    ["l_drill",        "L-Drill / 3-Cone (sec)"]
  ]},
  { key: "fms", title: "FMS · Functional Movement Screen", fields: [
    ["fms_deep_squat",            "Deep Squat (0-3)"],
    ["fms_hurdle_step",           "Hurdle Step (0-3)"],
    ["fms_inline_lunge",          "Inline Lunge (0-3)"],
    ["fms_shoulder_mobility",     "Shoulder Mobility (0-3)"],
    ["fms_aslr",                  "Active Straight-Leg Raise (0-3)"],
    ["fms_trunk_stability_pushup","Trunk Stability Push-Up (0-3)"],
    ["fms_rotary_stability",      "Rotary Stability (0-3)"],
    ["fms_total",                 "FMS Total (auto · 0-21)"],
  ]},
  { key: "scapular_ur", title: "Scapular Upward Rotation (°)", fields: [
    ["scap_ur_dom_60",     "Dominant @ 60° abd"],
    ["scap_ur_dom_90",     "Dominant @ 90° abd"],
    ["scap_ur_dom_120",    "Dominant @ 120° abd"],
    ["scap_ur_nondom_60",  "Non-Dominant @ 60° abd"],
    ["scap_ur_nondom_90",  "Non-Dominant @ 90° abd"],
    ["scap_ur_nondom_120", "Non-Dominant @ 120° abd"],
  ]},
  { key: "gird", title: "GIRD · Glenohumeral Internal-Rotation Deficit", fields: [
    ["gird_deg", "GIRD (°) — flag if ≥18° (clinically concerning)"],
  ]},
  { key: "orthopedic", title: "Orthopedic Special Tests", fields: [
    ["ortho_spurling",        "Spurling's Test"],
    ["ortho_obrien",          "O'Brien's Test"],
    ["ortho_hawkins_kennedy", "Hawkins-Kennedy"],
    ["ortho_yergason",        "Yergason's Test"],
    ["ortho_apprehension",    "Apprehension Test"],
    ["ortho_empty_can",       "Empty Can / Jobe's"],
    ["ortho_notes",           "Notes"],
  ]},
  { key: "ball_flight", title: "Ball Flight", fields: [
    ["pull_down_mph", "Pull Down (mph)"], ["mound_velocity", "Mound Velo (mph)"], ["flightscope_link", "FlightScope Link"], ["rapsodo_link", "Rapsodo Link"]
  ]},
];

const FREE_TEXT_KEYS = new Set(["year", "dominant_arm", "position", "bats", "throws", "injury_history", "flightscope_link", "rapsodo_link"]);

// Structured select-input fields with a fixed enum of options. When a key
// appears here, the baseline grid renders a <select> instead of a free Input.
const SELECT_OPTIONS = {
  position: [
    ["PO", "PO · Pitcher Only"], ["2-Way", "2-Way"], ["C", "C · Catcher"],
    ["1B", "1B"], ["2B", "2B"], ["3B", "3B"], ["SS", "SS"], ["OF", "OF · Outfield"],
  ],
  dominant_arm: [["R", "Right"], ["L", "Left"]],
  bats:        [["R", "Right"], ["L", "Left"], ["Both", "Switch · Both"]],
  throws:      [["R", "Right"], ["L", "Left"], ["Both", "Both"]],
};

export default function CoachPlayerDetail() {
  const { playerId } = useParams();
  const { team } = useAuth();
  const [detail, setDetail] = useState(null);
  const [trend, setTrend] = useState([]);
  const [profile, setProfile] = useState({});
  const [saving, setSaving] = useState(false);

  // ingestion
  const [ingestSource, setIngestSource] = useState("trackman");
  const [jsonText, setJsonText] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [ingestions, setIngestions] = useState([]);
  const [lastSummary, setLastSummary] = useState(null);
  const [lastTakeaways, setLastTakeaways] = useState([]);

  // Stalker quick-velo
  const [stalkerVelo, setStalkerVelo] = useState("");
  const [stalkerPitch, setStalkerPitch] = useState("fastball");
  const [stalkerBusy, setStalkerBusy] = useState(false);

  // Apple Health (Biometrics)
  const [biometrics, setBiometrics] = useState(null);

  // Readiness sparkline
  const [readinessTrend, setReadinessTrend] = useState([]);

  // Pitch Mechanics (coach view)
  const [pitchCapture, setPitchCapture] = useState(null);
  const [pitchTelemetry, setPitchTelemetry] = useState([]);
  const [pitchNorms, setPitchNorms] = useState({});

  const loadPitchMechanics = async () => {
    try {
      const { data } = await api.get(`/players/${playerId}/pitch-mechanics/latest`);
      setPitchCapture(data?.capture || null);
      setPitchTelemetry(data?.telemetry || []);
      setPitchNorms(data?.norms || {});
    } catch {/* no capture yet */}
  };

  const toggles = team?.baseline_toggles || {};

  const load = async () => {
    const [d, t, ing, bm, rt] = await Promise.all([
      api.get(`/players/${playerId}`),
      api.get(`/dashboard/workload/${playerId}`),
      api.get(`/players/${playerId}/ingestions`),
      api.get(`/apple-health/player/${playerId}`).catch(() => ({ data: null })),
      api.get(`/players/${playerId}/readiness/trend?days=14`).catch(() => ({ data: [] })),
    ]);
    setDetail(d.data);
    setProfile(d.data.profile || {});
    setIngestions(ing.data || []);
    setBiometrics(bm.data);
    setReadinessTrend((rt.data || []).map((x) => ({
      date: new Date(x.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
      score: x.score,
    })));
    setTrend(
      (t.data || []).map((l) => ({
        date: new Date(l.timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" }),
        volume: l.throw_volume || 0,
      })),
    );
  };

  useEffect(() => { load(); loadPitchMechanics(); }, [playerId]);

  const onChange = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(profile)) {
        if (v === "" || v == null) continue;
        if (FREE_TEXT_KEYS.has(k)) {
          payload[k] = v;
        } else if (typeof v === "object") {
          // skip nested summaries
          continue;
        } else {
          const n = Number(v);
          payload[k] = Number.isNaN(n) ? v : n;
        }
      }
      await api.patch(`/players/${playerId}/profile`, payload);
      toast.success("Baseline saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const ingestJson = async () => {
    setIngesting(true);
    setLastSummary(null);
    setLastTakeaways([]);
    try {
      const parsed = JSON.parse(jsonText);
      const { data } = await api.post(`/players/${playerId}/ingest`, { source: ingestSource, data: parsed });
      setLastSummary(data.summary);
      setLastTakeaways(data.takeaways || []);
      toast.success(`Ingested ${data.summary.pitches} rows from ${ingestSource}`);
      setJsonText("");
      load();
    } catch (err) {
      if (err instanceof SyntaxError) toast.error("Invalid JSON");
      else toast.error(formatApiError(err));
    } finally { setIngesting(false); }
  };

  const submitStalkerVelo = async () => {
    const v = parseFloat(stalkerVelo);
    if (!Number.isFinite(v) || v < 20 || v > 120) {
      toast.error("Velo must be between 20 and 120 mph");
      return;
    }
    setStalkerBusy(true);
    try {
      await api.post(`/players/${playerId}/stalker-velo`, { velocity_mph: v, pitch_type: stalkerPitch });
      toast.success(`Logged ${v} mph ${stalkerPitch}`);
      setStalkerVelo("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setStalkerBusy(false); }
  };

  const onFile = (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (e) => setJsonText(String(e.target?.result || ""));
    reader.readAsText(f);
  };

  const inputCls = "bg-[var(--ink-1)] border-[var(--ink-border)] text-white";

  if (!detail) return <div className="text-sm text-white/50" data-testid="player-detail-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="coach-player-detail-page">
      <Link to="/dashboard/roster" className="inline-flex items-center text-sm text-white/50 hover:text-white">
        <ChevronLeft className="h-4 w-4" /> Back to roster
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center font-display font-black text-white text-xl ring-1 ring-white/10" style={{ backgroundColor: "var(--brand-primary)" }}>
            {detail.player.name.split(" ").map((w) => w[0]).join("").slice(0, 2)}
          </div>
          <div>
            <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">{detail.player.email}</div>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white">{detail.player.name}</h1>
            <div className="text-sm text-white/50 mt-1">
              {detail.profile?.position || "—"} · {detail.profile?.year || "—"}
              {detail.profile?.bats ? ` · B/${detail.profile.bats}` : ""}
              {detail.profile?.throws ? ` · T/${detail.profile.throws}` : (detail.profile?.dominant_arm ? ` · ${detail.profile.dominant_arm}HP` : "")}
            </div>
            {Array.isArray(detail.profile?.imported_sources) && detail.profile.imported_sources.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="player-backfill-badges">
                {detail.profile.imported_sources.map((src) => {
                  const label = src === "armcare" ? "ArmCare" : src === "armoredheat" ? "Armored Heat" : src;
                  return (
                    <span
                      key={src}
                      data-testid={`player-backfill-badge-${src}`}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] tracking-[0.15em] uppercase font-bold border"
                      style={{ backgroundColor: "rgba(34,211,238,0.08)", borderColor: "rgba(34,211,238,0.3)", color: "#22D3EE" }}
                      title={`Historical data backfilled from ${label} via CSV importer.`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "#22D3EE" }} />
                      Backfilled · {label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <StatusBadge status={detail.status} />
          <div className="text-right">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">7-Day Volume</div>
            <div className="font-display font-black text-3xl font-mono-num text-white" data-testid="player-7day-volume">{detail.workload_7d}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl ink-card p-5">
        <div className="flex items-baseline justify-between">
          <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Readiness Score — 14 days</div>
          {readinessTrend.length > 0 && (() => {
            const latest = readinessTrend[readinessTrend.length - 1].score;
            const c = latest >= 80 ? "#16A34A" : latest >= 65 ? "#22D3EE" : latest >= 45 ? "#F59E0B" : "#EF4444";
            return <div className="font-display font-black text-2xl font-mono-num" style={{ color: c }} data-testid="readiness-latest">{Math.round(latest)}</div>;
          })()}
        </div>
        <div className="h-32 mt-3" style={{ minHeight: 128 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={128}>
            <AreaChart data={readinessTrend} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="readyGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} interval={2} />
              <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
              <Area type="monotone" dataKey="score" stroke="#22D3EE" strokeWidth={2.5} fill="url(#readyGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-xl ink-card p-5">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Acute Workload — 14 days</div>
        <div className="h-56 mt-3" style={{ minHeight: 224 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={224}>
            <AreaChart data={trend} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="volGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--brand-secondary)" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="var(--brand-secondary)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
              <Area type="monotone" dataKey="volume" stroke="var(--brand-secondary)" strokeWidth={2.5} fill="url(#volGrad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Tabs defaultValue="logs">
        <TabsList className="bg-[var(--ink-2)] border border-[var(--ink-border)]">
          <TabsTrigger value="logs" data-testid="tab-logs">Daily Logs</TabsTrigger>
          <TabsTrigger value="routine" data-testid="tab-routine">Routine</TabsTrigger>
          <TabsTrigger value="baseline" data-testid="tab-baseline">Baseline</TabsTrigger>
          <TabsTrigger value="biometrics" data-testid="tab-biometrics">Biometrics</TabsTrigger>
          <TabsTrigger value="ingest" data-testid="tab-ingest">Telemetry</TabsTrigger>
          <TabsTrigger value="pitch" data-testid="tab-pitch-mechanics">Pitch Mechanics</TabsTrigger>
        </TabsList>

        <TabsContent value="logs">
          <div className="mb-4">
            <InjuryRiskPanel playerUserId={playerId} />
          </div>
          <div className="mb-4">
            <InjuryEventLogger playerUserId={playerId} isCoach />
          </div>
          <div className="mb-4">
            <BodyScanHeatmap playerId={playerId} days={14} />
          </div>
          <div className="rounded-xl ink-card overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase border-b border-[var(--ink-border)]">
                <tr>
                  <th className="text-left px-3 py-2.5">Date</th>
                  <th className="text-center px-3 py-2.5">Arm</th>
                  <th className="text-center px-3 py-2.5">Sore</th>
                  <th className="text-center px-3 py-2.5">Sleep</th>
                  <th className="text-center px-3 py-2.5">Mental</th>
                  <th className="text-left px-3 py-2.5">Env</th>
                  <th className="text-right px-3 py-2.5">Vol</th>
                  <th className="text-left px-3 py-2.5">Body Scan</th>
                  <th className="text-right px-3 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--ink-border)] text-white/80">
                {detail.logs?.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-white/40">No logs yet.</td></tr>}
                {detail.logs?.slice(0, 30).map((l) => (
                  <tr key={l.log_id}>
                    <td className="px-3 py-2.5 whitespace-nowrap">{new Date(l.timestamp).toLocaleDateString()}</td>
                    <td className="px-3 py-2.5 text-center font-mono-num">{l.arm_health}</td>
                    <td className="px-3 py-2.5 text-center font-mono-num">{l.body_soreness}</td>
                    <td className="px-3 py-2.5 text-center font-mono-num">{l.sleep_quality}</td>
                    <td className="px-3 py-2.5 text-center font-mono-num">{l.mental_energy}</td>
                    <td className="px-3 py-2.5">{l.throwing_environment}</td>
                    <td className="px-3 py-2.5 text-right font-mono-num font-semibold text-white">{l.throw_volume}</td>
                    <td className="px-3 py-2.5 text-xs">
                      {(l.sharp_pain_zones || []).length > 0 ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                          style={{ backgroundColor: "rgba(220,38,38,0.18)", color: "#FCA5A5", border: "1px solid rgba(220,38,38,0.45)" }}
                          title={l.sharp_pain_zones.map((z) => z.label).join(", ")}
                          data-testid={`coach-sharp-pain-${l.log_id}`}>
                          ⚠ {l.sharp_pain_zones.map((z) => z.label).join(", ")}
                        </span>
                      ) : (l.body_scan?.length || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide"
                          style={{ backgroundColor: "rgba(245,158,11,0.15)", color: "#FCD34D", border: "1px solid rgba(245,158,11,0.35)" }}>
                          {l.body_scan.length} flagged
                        </span>
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right"><StatusBadge status={l.status} dot={false} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="routine">
          <RoutineOverrideBuilder playerId={playerId} playerName={detail.player?.name || "Athlete"} />
        </TabsContent>

        <TabsContent value="baseline">
          <div className="grid lg:grid-cols-2 gap-4 mb-4">
            <BaselineStaffEntryCard
              playerId={playerId}
              playerName={detail?.player?.name}
              dominantArm={profile.dominant_arm || "right"}
              onSaved={() => { load(); }}
            />
            <div className="rounded-xl ink-card p-5 h-full">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Manual baseline overrides</div>
              <p className="text-[12px] text-white/55 mt-2">
                The form on the left submits to the canonical screening endpoints and writes the full screening_event stream (severity findings, corrective protocols auto-assigned). Use the manual override grid below only when you need to edit a one-off legacy value.
              </p>
              <div className="flex justify-end mt-4">
                <Button onClick={save} disabled={saving} className="text-white font-bold red-glow" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="save-baseline-button">
                  <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save manual edits"}
                </Button>
              </div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {BASELINE_GROUPS.filter((g) => toggles[g.key] !== false).map((g) => (
              <div key={g.key} className="rounded-xl ink-card p-5">
                <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{g.title}</div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {g.fields.map(([k, label]) => {
                    const isOrtho = k.startsWith("ortho_") && k !== "ortho_notes";
                    const isOrthoNotes = k === "ortho_notes";
                    const selectOpts = SELECT_OPTIONS[k];
                    return (
                      <div key={k} className={(g.fields.length % 2 === 0 && !isOrthoNotes) ? "" : "col-span-2 md:col-span-1"}>
                        <Label className="text-xs text-white/60">{label}</Label>
                        {selectOpts ? (
                          <select
                            value={profile[k] ?? ""}
                            onChange={onChange(k)}
                            className={`mt-1 h-9 w-full rounded-md px-2 text-sm ${inputCls}`}
                            data-testid={`baseline-${k}`}
                          >
                            <option value="">—</option>
                            {selectOpts.map(([val, lbl]) => (
                              <option key={val} value={val}>{lbl}</option>
                            ))}
                          </select>
                        ) : isOrtho ? (
                          <select
                            value={profile[k] ?? "not_tested"}
                            onChange={onChange(k)}
                            className={`mt-1 h-9 w-full rounded-md px-2 text-sm ${inputCls}`}
                            data-testid={`baseline-${k}`}
                          >
                            <option value="not_tested">Not tested</option>
                            <option value="negative">Negative</option>
                            <option value="positive">Positive</option>
                          </select>
                        ) : (
                          <Input value={profile[k] ?? ""} onChange={onChange(k)}
                            className={`mt-1 h-9 ${inputCls}`}
                            readOnly={k === "fms_total"}
                            data-testid={`baseline-${k}`} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="biometrics">
          <BiometricsTab biometrics={biometrics} />
        </TabsContent>

        <TabsContent value="ingest">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-xl ink-card p-5">
              <div className="flex items-center gap-2 mb-2">
                <FileJson className="h-4 w-4" style={{ color: "var(--brand-secondary)" }} />
                <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Ingest pitch / biomech data</div>
              </div>
              <p className="text-sm text-white/60 mb-4">Paste JSON from any of the supported telemetry vendors. Per-source column shapes are auto-mapped.</p>

              <div className="mb-3">
                <Label className="text-xs text-white/60">Source</Label>
                <select
                  value={ingestSource}
                  onChange={(e) => setIngestSource(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md bg-[var(--ink-1)] border border-[var(--ink-border)] text-white text-sm px-2"
                  data-testid="ingest-source-select"
                >
                  <option value="trackman">TrackMan (per-pitch)</option>
                  <option value="643charts">6-4-3 Charts (splits / rollup)</option>
                  <option value="flightscope">FlightScope</option>
                  <option value="rapsodo">Rapsodo</option>
                  <option value="yakkertech">Yakkertech / SmartPark</option>
                  <option value="kvest">K-Vest (kinematic)</option>
                  <option value="playerdata">PlayerData (GPS / load)</option>
                  <option value="stalker">Stalker Pro (radar)</option>
                </select>
              </div>

              <textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                placeholder={
                  ingestSource === "trackman"
                    ? '[{"TaggedPitchType":"Fastball","RelSpeed":92.1,"SpinRate":2307,"SpinAxis":204,"Tilt":"3:24","InducedVertBreak":19.8,"HorzBreak":-2.9,"VertRelAngle":2.48,"Extension":5.97,"RelHeight":5.5,"PitchCall":"StrikeCalled"}]'
                    : ingestSource === "643charts"
                    ? '[{"Pitch Type":"FASTBALL","P":28,"Vel":86,"MxVel":88.9,"Spin":2280,"Tilt":"3:44","IVB":-6.4,"HB":18.2,"Strike%":"50.0%","Extension":5.83,"RelHeight":2.9}]'
                    : ingestSource === "flightscope"
                    ? '[{"pitchType":"FB","ballSpeed":94.2,"spinRate":2310,"verticalBreak":18.4,"horizontalBreak":-9.1,"releaseHeight":6.1,"extension":6.4}]'
                    : ingestSource === "rapsodo"
                    ? '[{"pitch_type":"FB","pitch_speed":94.2,"total_spin":2310,"spin_efficiency":89,"vert_break":18.4,"horz_break":-9.1,"release_height":6.1}]'
                    : ingestSource === "yakkertech"
                    ? '[{"PitchType":"FB","ReleaseSpeed":93,"SpinRate":2280,"InducedVertBreak":17,"HorzBreak":-8,"ExitSpeed":98,"LaunchAngle":14}]'
                    : ingestSource === "kvest"
                    ? '[{"ThrowType":"bullpen","Efficiency":78,"PelvisPeak":620,"TorsoPeak":1100,"ArmPeak":2300,"HandPeak":3900}]'
                    : ingestSource === "playerdata"
                    ? '[{"Duration":58,"Distance":3120,"Sprints":7,"MaxSpeed":24.1,"PlayerLoad":210,"HRavg":138,"HRmax":182}]'
                    : '[{"Speed":91.4,"PitchType":"fastball"}]'
                }
                rows={8}
                className="w-full rounded-md px-3 py-2 text-xs font-mono bg-[var(--ink-1)] border border-[var(--ink-border)] text-white placeholder:text-white/30 resize-y"
                data-testid="ingest-json-textarea"
              />
              <div className="flex items-center gap-3 mt-3">
                <Button onClick={ingestJson} disabled={!jsonText || ingesting} className="text-white font-semibold" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="ingest-submit-button">
                  <UploadIcon className="h-4 w-4 mr-2" />{ingesting ? "Ingesting…" : "Ingest"}
                </Button>
                <label className="text-sm text-white/60 cursor-pointer hover:text-white">
                  <input type="file" accept=".json,application/json" onChange={onFile} className="hidden" data-testid="ingest-file-input" />
                  …or pick a .json file
                </label>
              </div>
              {lastSummary && (
                <div className="mt-4 rounded-md border border-emerald-900/40 bg-emerald-950/30 p-4 text-sm text-emerald-200" data-testid="ingest-summary">
                  <div className="font-semibold flex items-center gap-2"><Save className="h-4 w-4" /> Ingested {lastSummary.pitches} rows · {lastSummary.source}</div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                    <div>Max velo: <span className="font-mono-num text-white">{lastSummary.max_velocity_mph ?? "—"}</span> mph</div>
                    <div>Avg velo: <span className="font-mono-num text-white">{lastSummary.avg_velocity_mph ?? "—"}</span> mph</div>
                    <div>Max spin: <span className="font-mono-num text-white">{lastSummary.max_spin_rpm ?? "—"}</span> rpm</div>
                    <div>Avg spin: <span className="font-mono-num text-white">{lastSummary.avg_spin_rpm ?? "—"}</span> rpm</div>
                    <div>Avg extension: <span className="font-mono-num text-white">{lastSummary.avg_extension_ft ?? "—"}</span> ft</div>
                    <div>Avg rel height: <span className="font-mono-num text-white">{lastSummary.avg_release_height_ft ?? "—"}</span> ft</div>
                  </div>
                </div>
              )}
              {lastTakeaways.length > 0 && (
                <div className="mt-3 rounded-md border border-cyan-900/40 bg-cyan-950/20 p-4 text-sm text-cyan-100" data-testid="ingest-takeaways">
                  <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-cyan-300 mb-2">What this means</div>
                  <ul className="space-y-1.5 text-xs">
                    {lastTakeaways.map((t, i) => <li key={i}>• {t}</li>)}
                  </ul>
                </div>
              )}

              {/* Stalker quick-velo inline form */}
              <div className="mt-5 pt-5 border-t border-[var(--ink-border)]" data-testid="stalker-quick-block">
                <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">Stalker quick-log · radar reading</div>
                <div className="flex items-end gap-2 flex-wrap">
                  <div className="flex-1 min-w-[110px]">
                    <Label className="text-xs text-white/60">Velo (mph)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      min="20" max="120"
                      value={stalkerVelo}
                      onChange={(e) => setStalkerVelo(e.target.value)}
                      placeholder="91.4"
                      className={`mt-1 h-9 ${inputCls}`}
                      data-testid="stalker-velo-input"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <Label className="text-xs text-white/60">Pitch type</Label>
                    <select
                      value={stalkerPitch}
                      onChange={(e) => setStalkerPitch(e.target.value)}
                      className="mt-1 w-full h-9 rounded-md bg-[var(--ink-1)] border border-[var(--ink-border)] text-white text-sm px-2"
                      data-testid="stalker-pitch-select"
                    >
                      <option value="fastball">Fastball</option>
                      <option value="sinker">Sinker</option>
                      <option value="cutter">Cutter</option>
                      <option value="slider">Slider</option>
                      <option value="curveball">Curveball</option>
                      <option value="changeup">Changeup</option>
                      <option value="splitter">Splitter</option>
                    </select>
                  </div>
                  <Button onClick={submitStalkerVelo} disabled={!stalkerVelo || stalkerBusy} data-testid="stalker-submit" className="h-9 text-white" style={{ backgroundColor: "var(--brand-primary)" }}>
                    {stalkerBusy ? "Logging…" : "Log velo"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="rounded-xl ink-card p-5">
              <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Ingestion history</div>
              {ingestions.length === 0 ? (
                <div className="mt-6 text-sm text-white/40">No prior ingestions.</div>
              ) : (
                <ul className="mt-3 divide-y divide-[var(--ink-border)]">
                  {ingestions.map((ing) => (
                    <li key={ing.ingestion_id} className="py-3" data-testid={`ingestion-row-${ing.ingestion_id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-sm text-white font-semibold uppercase tracking-wider">{ing.source}</div>
                          <div className="text-xs text-white/50">{new Date(ing.created_at).toLocaleString()}</div>
                        </div>
                        <div className="text-right text-sm">
                          <div className="font-mono-num text-white">{ing.summary?.pitches} {ing.summary?.pitches === 1 ? "row" : "rows"}</div>
                          <div className="text-xs text-white/50">max {ing.summary?.max_velocity_mph ?? "—"} mph</div>
                        </div>
                      </div>
                      {ing.takeaways && ing.takeaways.length > 0 && (
                        <ul className="mt-2 text-[11px] text-white/60 space-y-0.5">
                          {ing.takeaways.slice(0, 3).map((t, i) => <li key={i}>• {t}</li>)}
                        </ul>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </TabsContent>

        {/* PITCH MECHANICS — 2D + 3D capture + vendor telemetry fusion */}
        <TabsContent value="pitch">
          <div className="space-y-4">
            <div className="rounded-xl ink-card p-5" data-testid="coach-pitch-overview">
              <div className="flex items-baseline justify-between mb-3">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Pitch Mechanics · 2D + 3D capture</div>
                  <div className="font-display font-black text-xl text-white mt-1">
                    {pitchCapture
                      ? `${(pitchCapture.data?.handedness || "?").toUpperCase()}HP · ${new Date(pitchCapture.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                      : "No capture yet"}
                  </div>
                </div>
                <Video className="h-5 w-5 text-white/40" />
              </div>
              {pitchCapture ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries({
                      peak_ext_rotation_deg: ["Peak ER", "°"],
                      stride_length_pct: ["Stride", "%"],
                      trunk_tilt_release_deg: ["Trunk lean", "°"],
                      hip_shoulder_separation_deg: ["Hip-shoulder sep", "°"],
                      front_knee_flex_br_deg: ["Knee @ BR", "°"],
                      shoulder_abduction_deg: ["Shoulder abd", "°"],
                      head_tilt_max_deg: ["Head tilt (15°)", "°"],
                      head_drift_in: ["Head drift", "″"],
                    }).map(([k, [lbl, unit]]) => {
                      const v = pitchCapture.data?.[k];
                      const norm = pitchNorms[k];
                      const out = v != null && norm && (v < norm.low || v > norm.high);
                      return (
                        <div key={k} className="rounded p-2.5 bg-black/30 border border-white/10" data-testid={`coach-pitch-${k}`}>
                          <div className="text-[10px] text-white/55 uppercase tracking-wider">{lbl}</div>
                          <div className={`font-display font-bold text-base mt-0.5 ${out ? "text-amber-300" : "text-white"}`}>
                            {v == null ? "—" : `${v}${unit}`}
                          </div>
                          {norm && <div className="text-[10px] text-white/35">norm {norm.low}–{norm.high}</div>}
                        </div>
                      );
                    })}
                  </div>
                  {Array.isArray(pitchCapture.data?.flags) && pitchCapture.data.flags.length > 0 && (
                    <div className="mt-3 rounded p-2.5 border border-amber-500/30 bg-amber-500/5 text-xs text-amber-200">
                      <b>{pitchCapture.data.flags.length}</b> kinematic{pitchCapture.data.flags.length === 1 ? "" : "s"} outside the elite window —
                      review the Why pane on the Baseline tab for corrective seeding.
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-white/55">
                  This athlete hasn&apos;t recorded a pitch-mechanics capture yet. Ask them to open the <Link to="/pitch-mechanics" className="text-cyan-300 underline">/pitch-mechanics</Link> page to record one, then come back to attach FlightScope / TrackMan / Rapsodo telemetry here.
                </p>
              )}
            </div>

            {pitchCapture?.event_id && (
              <PitchTelemetryImporter
                captureId={pitchCapture.event_id}
                onAttached={() => loadPitchMechanics()}
              />
            )}

            {pitchTelemetry.length > 0 && (
              <div className="rounded-xl ink-card p-5" data-testid="coach-telemetry-ledger">
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)] mb-2">
                  Linked Telemetry · {pitchTelemetry.reduce((a, t) => a + (t.summary?.pitches || 0), 0)} pitches across {pitchTelemetry.length} ingestion{pitchTelemetry.length === 1 ? "" : "s"}
                </div>
                <div className="space-y-2">
                  {pitchTelemetry.map((ing, i) => {
                    const s = ing.summary || {};
                    return (
                      <div key={ing.ingestion_id || i} className="rounded p-3 bg-black/30 border border-white/10 flex items-center justify-between" data-testid={`coach-ingestion-${i}`}>
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded bg-[var(--brand-secondary)]/15 text-white">
                            {(s.source || ing.source || "?").toUpperCase()}
                          </span>
                          <span className="text-sm text-white font-semibold">{s.pitches || (ing.pitches?.length ?? 0)} pitches</span>
                        </div>
                        <div className="flex items-baseline gap-3 text-[11px] font-mono-num text-white/75">
                          {s.max_velocity_mph != null && <span><span className="text-white/40">peak</span> {s.max_velocity_mph} mph</span>}
                          {s.avg_velocity_mph != null && <span><span className="text-white/40">avg</span> {s.avg_velocity_mph}</span>}
                          {s.avg_spin_rpm != null && <span><span className="text-white/40">spin</span> {Math.round(s.avg_spin_rpm)}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BiometricsTab({ biometrics }) {
  if (!biometrics || (!biometrics.daily?.length && !biometrics.workouts?.length)) {
    return (
      <div className="rounded-xl ink-card p-10 text-center text-white/50" data-testid="biometrics-empty">
        <HeartPulse className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--brand-secondary)" }} />
        <div className="font-semibold text-white">No Apple Watch data yet</div>
        <div className="text-sm mt-1">The athlete hasn&apos;t connected their Apple Watch. Ask them to open <code className="bg-[var(--ink-1)] px-1.5 py-0.5 rounded text-xs">/portal/apple-health</code> and follow the setup steps.</div>
      </div>
    );
  }
  const sorted = [...(biometrics.daily || [])].sort((a, b) => a.date.localeCompare(b.date));
  const chart = sorted.slice(-14).map((d) => ({
    date: new Date(d.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }),
    hrv: d.hrv_avg,
    resting_hr: d.resting_hr,
    sleep: d.sleep_hours,
    vo2: d.vo2_max,
  }));
  const latest = sorted[sorted.length - 1] || {};

  return (
    <div className="space-y-4" data-testid="biometrics-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["Resting HR", latest.resting_hr?.toFixed(0), "bpm", "#EF4444"],
          ["HRV", latest.hrv_avg?.toFixed(0), "ms", "#22D3EE"],
          ["Sleep", latest.sleep_hours?.toFixed(1), "hrs", "#A78BFA"],
          ["VO₂ Max", latest.vo2_max?.toFixed(1), "ml/kg/min", "#F59E0B"],
        ].map(([label, value, unit, color]) => (
          <div key={label} className="rounded-xl ink-card p-4">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">{label}</div>
            <div className="mt-2 flex items-baseline gap-1.5">
              <div className="font-display font-black text-3xl font-mono-num" style={{ color }}>{value ?? "—"}</div>
              {value != null && <div className="text-xs text-white/40">{unit}</div>}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl ink-card p-5">
        <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">Recovery telemetry — 14 days</div>
        <div className="h-56 mt-3" style={{ minHeight: 224 }}>
          <ResponsiveContainer width="100%" height="100%" minHeight={224}>
            <AreaChart data={chart}>
              <defs>
                <linearGradient id="hrvGradC" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#22D3EE" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#1F3354" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #2C4570", backgroundColor: "#0F1E36", color: "#fff" }} />
              <Area type="monotone" dataKey="hrv" stroke="#22D3EE" strokeWidth={2.5} fill="url(#hrvGradC)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {biometrics.workouts?.length > 0 && (
        <div className="rounded-xl ink-card overflow-hidden">
          <div className="px-5 py-3 text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase border-b border-[var(--ink-border)]">Apple Watch workouts</div>
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase border-b border-[var(--ink-border)]">
              <tr>
                <th className="text-left px-3 py-2.5">Date</th>
                <th className="text-left px-3 py-2.5">Activity</th>
                <th className="text-right px-3 py-2.5">Duration</th>
                <th className="text-right px-3 py-2.5">kcal</th>
                <th className="text-right px-3 py-2.5">Avg HR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--ink-border)] text-white/80">
              {biometrics.workouts.slice(0, 15).map((w, i) => (
                <tr key={i}>
                  <td className="px-3 py-2.5 whitespace-nowrap">{w.date}</td>
                  <td className="px-3 py-2.5">{w.name}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num">{w.duration_sec ? Math.round(w.duration_sec / 60) + " min" : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num">{w.active_energy_kcal ? Math.round(w.active_energy_kcal) : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono-num">{w.hr_avg ? Math.round(w.hr_avg) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-xs text-white/40 pt-1">
        Last sync: {biometrics.last_sync ? new Date(biometrics.last_sync).toLocaleString() : "Never"}
      </div>
    </div>
  );
}

